import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Paper,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    Alert,
    CircularProgress,
} from '@mui/material';
import { staffSelfSignup } from '../api/auth';
import { LINKS } from '../lib/links';
import Header from './Header';
import Footer from './Footer';

// Theme colors from therapist signup design
const colors = {
    blueDark: '#1a4f8a',
    blueMain: '#1a73c8',
    blueMid: '#2a8fd4',
    blueLight: '#3ab3e8',
    bluePale: '#e8f4fd',
    blueAccent: '#0dd4f0',
    white: '#ffffff',
    greySoft: '#f5f8fc',
    greyText: '#5a6e84',
    dark: '#0d1f35',
    border: '#d0e4f5',
};

const SPECIALISATIONS = [
    'Physiotherapy',
    'Occupational Therapy',
    'Speech Therapy',
    'Nurse',
    'Caregiver',
    'Neuro Therapy',
    'Paediatric Therapy',
    'Yoga Therapy',
    'Other',
];

const BENEFIT_ITEMS = [
    { icon: '📱', title: 'Mobile-First Workflow', body: 'No laptop, no desktop, no extra steps. Everything you need to manage your day is on your phone.' },
    { icon: '🗂️', iconImg: 'medicalfile.png', title: 'Structured Session Records', body: 'Session notes, consent forms, and progress records filed correctly every time. Nothing missing, nothing out of place.' },
    { icon: '📦', iconImg: 'checklist.png', title: 'Package & Session Tracking', body: 'See every session against the patient\'s package. Know what\'s been done, what\'s pending, and what\'s due next.' },
    { icon: '🗺️', iconImg: 'maproute.png', title: 'Automatic Travel Logging', body: 'Every home visit is logged automatically via GPS. Your travel claims are accurate and verifiable without manual entry.' },
    { icon: '🔔', title: 'Smart Reminders', body: 'End-of-day reminders flag any sessions left incomplete. Nothing slips through at the end of a long day.' },
    { icon: '📊', title: 'Performance Visibility', body: 'Your rehab center admin can track what you delivered and when. Your work is on record, not just in memory.' },
];

// Scroll-triggered animation hook
const useInView = (options = {}) => {
    const [inView, setInView] = useState(false);
    const ref = useRef(null);
    const { rootMargin = '0px 0px -80px 0px', threshold = 0.1 } = options;
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setInView(true);
            },
            { rootMargin, threshold }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [rootMargin, threshold]);
    return [ref, inView];
};

const SignupFormPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [section2Ref, section2InView] = useInView();
    const [section3Ref, section3InView] = useInView();

    const scrollSignupFormIntoView = useCallback(() => {
        const el = document.getElementById('signup-form');
        if (!el) return;
        const headerOffset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top, behavior: 'smooth' });
    }, []);

    const scrollToPageTop = useCallback(() => {
        window.scrollTo(0, 0);
    }, []);

    // Legacy: deep links with #signup-form
    useEffect(() => {
        if (location.hash !== '#signup-form') return;
        scrollSignupFormIntoView();
    }, [location.hash, scrollSignupFormIntoView]);

    // Header "See how it works" — scroll to form without putting # in the URL
    useEffect(() => {
        if (!location.state?.scrollToSignupForm) return;
        scrollSignupFormIntoView();
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate, scrollSignupFormIntoView]);

    const [formData, setFormData] = useState({
        fullName: '',
        mobile: '',
        email: '',
        specialisation: '',
        specialisationOther: '',
        experienceYears: '',
        city: '',
    });
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successData, setSuccessData] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
        if (formError) setFormError('');
    };

    const handleMobileChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setFormData((prev) => ({ ...prev, mobile: value }));
        if (errors.mobile) setErrors((prev) => ({ ...prev, mobile: '' }));
        if (formError) setFormError('');
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.fullName.trim()) newErrors.fullName = 'Required';
        if (!formData.mobile) newErrors.mobile = 'Required';
        else if (formData.mobile.length !== 10) newErrors.mobile = 'Enter valid 10-digit number';
        if (!formData.email.trim()) newErrors.email = 'Required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Enter a valid email';
        if (!formData.specialisation) newErrors.specialisation = 'Required';
        if (formData.specialisation === 'Other' && !formData.specialisationOther.trim()) newErrors.specialisationOther = 'Please specify your specialisation';
        const expNum = formData.experienceYears === '' ? NaN : Number(formData.experienceYears);
        if (formData.experienceYears === '' || isNaN(expNum) || expNum < 0) newErrors.experienceYears = 'Enter years of experience (0 or more)';
        if (!formData.city.trim()) newErrors.city = 'Required';
        setErrors(newErrors);
        setFormError('');
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        setFormError('');
        setErrors({});
        const name = formData.fullName.trim();
        const contact = formData.mobile ? `+91${formData.mobile}` : '';
        const expNum = formData.experienceYears === '' ? undefined : Number(formData.experienceYears);
        const payload = {
            name,
            email: formData.email.trim(),
            contact,
            specialization: formData.specialisation === 'Other' ? formData.specialisationOther.trim() : (formData.specialisation || undefined),
            experience_years: (expNum !== undefined && !isNaN(expNum) && expNum >= 0) ? expNum : undefined,
        };
        try {
            const data = await staffSelfSignup(payload);
            setSuccessData(data);
        } catch (err) {
            const backend = err.data;
            if (backend?.errors && Array.isArray(backend.errors)) {
                const fieldErrors = {};
                backend.errors.forEach(({ path, msg }) => {
                    if (path === 'name') fieldErrors.fullName = msg;
                    else if (path === 'contact') fieldErrors.mobile = msg;
                    else fieldErrors[path] = msg;
                });
                setErrors(fieldErrors);
            }
            setFormError(err.message || 'Signup failed');
        } finally {
            setLoading(false);
        }
    };

    const handleGoToLogin = () => {
        navigate('/');
    };

    const inputSx = {
        border: '1.5px solid',
        borderColor: colors.border,
        borderRadius: '10px',
        '& .MuiOutlinedInput-root': {
            bgcolor: colors.greySoft,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            '&.Mui-focused': { bgcolor: colors.white },
            '& fieldset': { border: 'none' },
        },
        '& .MuiInputLabel-shrink': { color: colors.blueDark },
    };

    const labelSx = {
        fontSize: 12,
        fontWeight: 600,
        color: colors.blueDark,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        mb: 0.5,
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.greySoft, color: colors.dark, fontFamily: "'DM Sans', sans-serif" }}>
            <Header />

            {/* HERO — padding top clears fixed header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${colors.blueDark} 0%, ${colors.blueMain} 55%, ${colors.blueMid} 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    pt: { xs: 10, sm: 12 },
                    pb: { xs: 4, sm: 6 },
                    px: { xs: 2, sm: 5 },
                }}
            >
                {/* Subtle gradient orbs */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(ellipse 600px 400px at 80% 50%, rgba(58,179,232,0.18), transparent), radial-gradient(ellipse 300px 300px at 10% 80%, rgba(255,255,255,0.05), transparent)',
                    }}
                />
                {/* Decorative circle (like HTML ::after) */}
                <Box
                    sx={{
                        position: 'absolute',
                        right: -60,
                        top: -60,
                        width: 500,
                        height: 500,
                        borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.06)',
                        pointerEvents: 'none',
                    }}
                />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 420px' },
                            gap: 4,
                            alignItems: 'center',
                            maxWidth: 1100,
                            mx: 'auto',
                        }}
                    >
                        {/* Left: copy */}
                        <Box>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    bgcolor: 'rgba(255,255,255,0.12)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '20px',
                                    py: 0.75,
                                    px: 1.75,
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        bgcolor: colors.blueAccent,
                                        borderRadius: '50%',
                                        animation: 'pulse 2s ease-in-out infinite',
                                        '@keyframes pulse': {
                                            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                            '50%': { opacity: 0.6, transform: 'scale(1.3)' },
                                        },
                                    }}
                                />
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.9)', fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase' }}>
                                    NOW ONBOARDING THERAPISTS
                                </Typography>
                            </Box>
                            <Typography
                                component="h1"
                                sx={{
                                    fontFamily: "'Sora', sans-serif",
                                    fontSize: { xs: 32, sm: 42, md: 52 },
                                    fontWeight: 800,
                                    color: colors.white,
                                    lineHeight: 1.12,
                                    letterSpacing: -1,
                                    mb: 2,
                                }}
                            >
                                Your Work. <span style={{ color: colors.blueAccent }}>Tracked,</span> Documented, Paid Right.
                            </Typography>
                            <Typography sx={{ fontSize: 17, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, maxWidth: 520, mb: 3, fontWeight: 300 }}>
                            meDZhub is a free digital workspace for home-visit therapists. Manage your sessions, document your work, log your travel, and get paid accurately, from your phone. Clean records mean fewer patient complaints and smoother care delivery.
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', fontWeight: 500, letterSpacing: 0.5, mb: 1 }}>
                                DPDP-Ready | Free to Join | Built for Home Visits
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>
                                Available on Android and iOS
                            </Typography>
                        </Box>

                        {/* Form card */}
                        <Paper
                            id="signup-form"
                            elevation={0}
                            sx={{
                                bgcolor: colors.white,
                                borderRadius: '20px',
                                p: 3,
                                boxShadow: '0 24px 60px rgba(13,31,53,0.25)',
                            }}
                        >
                            {!successData && (
                                <>
                                    <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, color: colors.blueDark, mb: 0.5 }}>
                                        Create Your Therapist Profile
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: colors.greyText, mb: 2 }}>
                                    Fill in your details to get started.
                                    </Typography>
                                </>
                            )}
                            {successData ? (
                                <Box>
                                    <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 700, color: colors.blueDark, mb: 1.5, textAlign: 'center' }}>
                                        Thank you for registering!
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: colors.greyText, mb: 1.5, textAlign: 'center' }}>
                                        Credentials have been sent to your email. You can log in now.
                                    </Typography>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        {successData.message}
                                    </Alert>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.blueDark, mb: 1.5 }}>
                                        Download the app
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                                        <Box
                                            component="a"
                                            href={LINKS.PLAY_STORE_APP}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                display: 'block',
                                                textDecoration: 'none',
                                                maxWidth: 200,
                                                transition: 'opacity 0.2s, transform 0.2s',
                                                '&:hover': { opacity: 0.9, transform: 'scale(1.02)' },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={`${process.env.PUBLIC_URL}/assets/Play Store.jpg`}
                                                alt="Get it on Google Play"
                                                sx={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        </Box>
                                        <Box
                                            component="a"
                                            href={LINKS.APP_STORE_APP}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                display: 'block',
                                                textDecoration: 'none',
                                                maxWidth: 200,
                                                transition: 'opacity 0.2s, transform 0.2s',
                                                '&:hover': { opacity: 0.9, transform: 'scale(1.02)' },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={`${process.env.PUBLIC_URL}/assets/App Store.jpg`}
                                                alt="Download on the App Store"
                                                sx={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Button
                                        fullWidth
                                        onClick={handleGoToLogin}
                                        sx={{
                                            py: 1.75,
                                            background: `linear-gradient(135deg, ${colors.blueMain}, ${colors.blueLight})`,
                                            color: colors.white,
                                            fontFamily: "'Sora', sans-serif",
                                            fontSize: 15,
                                            fontWeight: 700,
                                            borderRadius: '12px',
                                            '&:hover': { background: `linear-gradient(135deg, ${colors.blueMain}, ${colors.blueLight})`, boxShadow: '0 8px 24px rgba(26,115,200,0.35)' },
                                        }}
                                    >
                                        Go to Login →
                                    </Button>
                                </Box>
                            ) : (
                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                {formError && (
                                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
                                        {formError}
                                    </Alert>
                                )}
                                <Box sx={{ mb: 2 }}>
                                    <Typography component="label" sx={labelSx}>Full Name *</Typography>
                                    <TextField
                                        fullWidth
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        placeholder="Priya Sharma"
                                        error={!!errors.fullName}
                                        helperText={errors.fullName}
                                        sx={inputSx}
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Typography component="label" sx={labelSx}>Mobile Number *</Typography>
                                    <TextField
                                        fullWidth
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleMobileChange}
                                        placeholder="98765 43210"
                                        error={!!errors.mobile}
                                        helperText={errors.mobile}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ color: colors.greyText }}>
                                                    +91
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={inputSx}
                                        size="small"
                                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                    />
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Typography component="label" sx={labelSx}>Email Address *</Typography>
                                    <TextField
                                        fullWidth
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="priya@example.com"
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        sx={inputSx}
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ mb: formData.specialisation === 'Other' ? 2 : 2 }}>
                                    <Typography component="label" sx={labelSx}>Specialisation *</Typography>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        error={!!errors.specialisation}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: colors.greySoft,
                                                borderRadius: '10px',
                                                border: '1.5px solid',
                                                borderColor: colors.border,
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontSize: 14,
                                                '&.Mui-focused': { bgcolor: colors.white, borderColor: colors.blueMain },
                                            },
                                        }}
                                    >
                                        <Select
                                            name="specialisation"
                                            value={formData.specialisation}
                                            onChange={handleChange}
                                            displayEmpty
                                            renderValue={(v) => v || 'Select your specialisation'}
                                            sx={{ color: formData.specialisation ? colors.dark : colors.greyText }}
                                            MenuProps={{
                                                disableScrollLock: true,
                                                sx: { zIndex: 9999 },
                                                PaperProps: { sx: { mt: 1.5, maxHeight: 320 } },
                                            }}
                                        >
                                            <MenuItem value="" disabled>Select your specialisation</MenuItem>
                                            {SPECIALISATIONS.map((opt) => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{errors.specialisation}</FormHelperText>
                                    </FormControl>
                                </Box>
                                {formData.specialisation === 'Other' && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography component="label" sx={labelSx}>Please specify (other) *</Typography>
                                        <TextField
                                            fullWidth
                                            name="specialisationOther"
                                            value={formData.specialisationOther}
                                            onChange={handleChange}
                                            placeholder="e.g. Sports therapy, Geriatric care"
                                            error={!!errors.specialisationOther}
                                            helperText={errors.specialisationOther}
                                            sx={inputSx}
                                            size="small"
                                        />
                                    </Box>
                                )}
                                <Box sx={{ mb: 2 }}>
                                    <Typography component="label" sx={labelSx}>Years of Experience *</Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        name="experienceYears"
                                        value={formData.experienceYears}
                                        onChange={handleChange}
                                        placeholder="0"
                                        error={!!errors.experienceYears}
                                        helperText={errors.experienceYears}
                                        sx={inputSx}
                                        size="small"
                                        inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                                    />
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Typography component="label" sx={labelSx}>City / Location *</Typography>
                                    <TextField
                                        fullWidth
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        placeholder="Bengaluru, Hyderabad…"
                                        error={!!errors.city}
                                        helperText={errors.city}
                                        sx={inputSx}
                                        size="small"
                                    />
                                </Box>
                                
                                <Button
                                    type="submit"
                                    fullWidth
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                                    id="Submit-button"
                                    data-gtm="Submit-button"
                                    sx={{
                                        mt: 1,
                                        py: 1.75,
                                        background: `linear-gradient(135deg, ${colors.blueMain}, ${colors.blueLight})`,
                                        color: colors.white,
                                        fontFamily: "'Sora', sans-serif",
                                        fontSize: 15,
                                        fontWeight: 700,
                                        borderRadius: '12px',
                                        letterSpacing: 0.3,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, ${colors.blueMain}, ${colors.blueLight})`,
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 8px 24px rgba(26,115,200,0.35)',
                                        },
                                    }}
                                >
                                    {loading ? 'Registering…' : 'Create Account →'}
                                </Button>
                                <Typography sx={{ fontSize: 11, color: colors.greyText, textAlign: 'center', mt: 1.5, lineHeight: 1.5 }}>
                                    By registering you agree to our <Link to="/terms" style={{ color: colors.blueMain, textDecoration: 'none' }}>Terms of Service</Link> and <Link to="/privacy" style={{ color: colors.blueMain, textDecoration: 'none' }}>Privacy Policy</Link>.<br />
                                    Your data is protected under India's <strong>Digital Personal Data Protection (DPDP) Act, 2023</strong>.
                                </Typography>
                            </Box>
                            )}
                        </Paper>
                    </Box>
                </Container>
            </Box>

            {/* MAIN */}
            <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 5 }, py: 6 }}>
                {/* Section 2 — What meDZhub does for you */}
                <Box
                    ref={section2Ref}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 4,
                        alignItems: 'center',
                        mb: 8,
                    }}
                >
                    <Box
                        sx={{
                            opacity: section2InView ? 1 : 0,
                            transform: section2InView ? 'translateX(0)' : 'translateX(-32px)',
                            transition: 'opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        }}
                    >
                        <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: 2, color: colors.blueMain, mb: 1, textTransform: 'uppercase' }}>
                            WHAT IS MEDZHUB?
                        </Typography>
                        <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: { xs: 24, md: 36 }, fontWeight: 800, color: colors.dark, letterSpacing: -0.5, lineHeight: 1.2, mb: 1.5 }}>
                        One app to manage all your home therapy work.
                        </Typography>
                        <Typography sx={{ fontSize: 16, color: colors.greyText, lineHeight: 1.7, maxWidth: 600 }}>
                            You're doing real clinical work: in patient homes, on the move, across multiple cases. But most of the tools built for therapists were designed for clinics.
                        </Typography>
                        <Typography sx={{ fontSize: 16, color: colors.greyText, lineHeight: 1.7, maxWidth: 600, mt: 1.5 }}>
                            meDZhub is different. It's built around how home-visit therapy actually works: session by session, location by location, patient by patient. No paper files. No WhatsApp confusion. No payment disputes at the end of the month.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            bgcolor: colors.bluePale,
                            background: `linear-gradient(135deg, ${colors.bluePale} 0%, #dbeeff 100%)`,
                            borderRadius: '20px',
                            p: 3,
                            position: 'relative',
                            overflow: 'hidden',
                            opacity: section2InView ? 1 : 0,
                            transform: section2InView ? 'translateX(0)' : 'translateX(32px)',
                            transition: 'opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s, transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
                        }}
                    >
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            {[
                                { iconImg: 'calenderclock.png', label: 'Smart Appointment Scheduling', body: 'Know exactly where you need to be and when. No conflicts, no last-minute confusion.' },
                                { iconImg: 'checklist.png', label: 'Session and Package Tracking', body: 'See every session against the patient\'s package. Know what\'s been done and what\'s pending.' },
                                { iconImg: 'maproute.png', label: 'Live Trip and GPS Logging', body: 'Your travel is logged automatically every time you head to a patient. Your claims are accurate and verifiable.' },
                                { iconImg: 'medicalfile.png', label: 'Document Upload and Records', body: 'Upload consent forms, session notes, and progress records in the right format, at the right time.' },
                            ].map((item, index) => (
                                <Box
                                    key={item.label}
                                    sx={{
                                        bgcolor: colors.white,
                                        borderRadius: '14px',
                                        p: 2,
                                        boxShadow: '0 4px 16px rgba(26,115,200,0.1)',
                                        opacity: section2InView ? 1 : 0,
                                        transform: section2InView ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.96)',
                                        transition: `opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.25 + index * 0.1}s, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.25 + index * 0.1}s, box-shadow 0.3s ease`,
                                        '&:hover': { boxShadow: '0 12px 28px rgba(26,115,200,0.18)' },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}/assets/${item.iconImg}`}
                                        alt=""
                                        sx={{ width: 40, height: 40, mb: 1, objectFit: 'contain' }}
                                    />
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.blueDark, lineHeight: 1.4, mb: 0.75 }}>{item.label}</Typography>
                                    <Typography sx={{ fontSize: 11, color: colors.greyText, lineHeight: 1.5 }}>{item.body}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Section 3 — How it works */}
                <Box ref={section3Ref} sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        sx={{
                            fontSize: 15,
                            fontWeight: 700,
                            letterSpacing: 2,
                            textTransform: 'uppercase',
                            color: colors.blueMain,
                            mb: 1,
                            opacity: section3InView ? 1 : 0,
                            transform: section3InView ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                        }}
                    >
                        HOW IT WORKS
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Sora', sans-serif",
                            fontSize: { xs: 24, md: 36 },
                            fontWeight: 800,
                            color: colors.dark,
                            letterSpacing: -0.5,
                            lineHeight: 1.2,
                            mb: 1.5,
                            opacity: section3InView ? 1 : 0,
                            transform: section3InView ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s ease-out 0.1s, transform 0.6s ease-out 0.1s',
                        }}
                    >
                        Three steps to get started
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 16,
                            color: colors.greyText,
                            lineHeight: 1.7,
                            maxWidth: 600,
                            mx: 'auto',
                            mb: 4,
                            opacity: section3InView ? 1 : 0,
                            transform: section3InView ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s',
                        }}
                    >
                        Simple signup. No paperwork.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                        {[
                            { num: 1, title: 'Register', body: 'Fill in your basic details: name, specialisation, experience, and location.' },
                            { num: 2, title: 'Get onboarded', body: 'Your login credentials are sent to your registered email. Download the app and log in.' },
                            { num: 3, title: 'Log in and start', body: 'Set your availability, and start conducting sessions from the app.' },
                        ].map((step, index) => (
                            <Box
                                key={step.num}
                                sx={{
                                    bgcolor: colors.white,
                                    borderRadius: '16px',
                                    p: 2.5,
                                    border: `1.5px solid ${colors.border}`,
                                    position: 'relative',
                                    textAlign: 'center',
                                    opacity: section3InView ? 1 : 0,
                                    transform: section3InView ? 'translateY(0) scale(1)' : 'translateY(28px) scale(0.97)',
                                    transition: `opacity 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.3 + index * 0.15}s, transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.3 + index * 0.15}s, box-shadow 0.3s ease`,
                                    '&:hover': { transform: 'translateY(-6px) scale(1.02)', boxShadow: '0 16px 40px rgba(26,115,200,0.16)' },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        mx: 'auto',
                                        mb: 2,
                                        background: `linear-gradient(135deg, ${colors.blueMain}, ${colors.blueLight})`,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontFamily: "'Sora', sans-serif",
                                        fontSize: 14,
                                        fontWeight: 800,
                                        color: colors.white,
                                        boxShadow: `0 4px 14px ${colors.blueMain}50`,
                                    }}
                                >
                                    {step.num}
                                </Box>
                                <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: 14, fontWeight: 700, color: colors.dark, mb: 1 }}>{step.title}</Typography>
                                <Typography sx={{ fontSize: 13, color: colors.greyText, lineHeight: 1.6 }}>{step.body}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Benefits */}
                <Box sx={{ mb: 8 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: 2, color: colors.blueMain, mb: 1 }}>
                        Why meDZhub?
                    </Typography>
                    <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: { xs: 24, md: 36 }, fontWeight: 800, color: colors.dark, letterSpacing: -0.5, lineHeight: 1.2, mb: 3 }}>
                      Your work, your records, your pay. All accounted for.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                        {BENEFIT_ITEMS.map((item) => (
                            <Box
                                key={item.title}
                                sx={{
                                    bgcolor: colors.white,
                                    borderRadius: '16px',
                                    p: 2.5,
                                    border: `1.5px solid ${colors.border}`,
                                    transition: 'border-color 0.2s, transform 0.2s',
                                    '&:hover': { borderColor: colors.blueLight, transform: 'translateY(-3px)' },
                                }}
                            >
                                <Box sx={{ width: 48, height: 48, bgcolor: colors.bluePale, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, mb: 2 }}>
                                    {item.iconImg ? (
                                        <Box component="img" src={`${process.env.PUBLIC_URL}/assets/${item.iconImg}`} alt="" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
                                    ) : (
                                        item.icon
                                    )}
                                </Box>
                                <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 700, color: colors.dark, mb: 1 }}>{item.title}</Typography>
                                <Typography sx={{ fontSize: 14, color: colors.greyText, lineHeight: 1.6 }}>{item.body}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* DPDP Banner */}
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${colors.blueDark} 0%, #0f3a6e 100%)`,
                        borderRadius: '20px',
                        p: { xs: 3, sm: 4 },
                        mb: 8,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                        gap: 4,
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: 'rgba(13,212,240,0.12)',
                                border: '1px solid rgba(13,212,240,0.3)',
                                borderRadius: '20px',
                                py: 0.6,
                                px: 1.5,
                                mb: 1.5,
                            }}
                        >
                            <Typography sx={{ fontSize: 11, color: colors.blueAccent, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                                Data Privacy & Compliance
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: 24, fontWeight: 800, color: colors.white, mb: 1.5 }}>
                        Your data. Your patients' data. Protected
                        </Typography>
                        <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.72)', lineHeight: 1.65, maxWidth: 560 }}>
                            meDZhub is fully compliant with India's <strong style={{ color: colors.white }}>Digital Personal Data Protection (DPDP) Act, 2023.</strong> Every piece of patient information, every session record, and every document you upload is handled to the strictest data governance standards. DPDP compliance is not an afterthought. It's built into the platform from day one.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1.5, flexWrap: 'wrap' }}>
                        {[
                            { icon: '🔒', label: 'DPDP Ready' },
                            { icon: '✅', label: 'Consent\nManagement' },
                            { icon: '🛡️', label: 'Secure Data\nStorage' },
                        ].map((b) => (
                            <Box
                                key={b.label}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(255,255,255,0.14)',
                                    borderRadius: '12px',
                                    py: 1.5,
                                    px: 2,
                                    textAlign: 'center',
                                    minWidth: 160,
                                }}
                            >
                                <Typography sx={{ fontSize: 20, mb: 0.5 }}>{b.icon}</Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 600, whiteSpace: 'pre-line' }}>{b.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Section 6 — Closing CTA */}
                <Box
                    sx={{
                        bgcolor: colors.bluePale,
                        borderRadius: '20px',
                        p: 4,
                        mb: 8,
                        textAlign: 'center',
                        border: `1.5px solid ${colors.border}`,
                    }}
                >
                    <Typography sx={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 700, color: colors.blueDark, lineHeight: 1.45, maxWidth: 700, mx: 'auto', mb: 2 }}>
                        Home-based therapy is one of India's fastest-growing care models. Run your end of it properly.
                    </Typography>
                    <Button
                        type="button"
                        variant="contained"
                        size="large"
                        onClick={scrollToPageTop}
                        sx={{
                            mt: 1,
                            mb: 2,
                            py: 1.5,
                            px: 3,
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: 700,
                            fontSize: 16,
                            background: `linear-gradient(135deg, ${colors.blueMain}, ${colors.blueLight})`,
                            boxShadow: `0 4px 20px ${colors.blueMain}40`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${colors.blueDark}, ${colors.blueMain})`,
                                boxShadow: `0 6px 24px ${colors.blueMain}50`,
                            },
                        }}
                    >
                        Create Account
                    </Button>
                </Box>
            </Box>

            <Footer />
        </Box>
    );
};

export default SignupFormPage;
